import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, MapPin, Check, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { SEO_PAGES } from "@/lib/seoPages";

const HERO_IMG =
  "https://images.pexels.com/photos/34182370/pexels-photo-34182370.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function SeoLandingPage({ configKey }) {
  const cfg = SEO_PAGES[configKey];
  useSeo({ title: cfg.title, description: cfg.metaDescription, path: cfg.path });

  return (
    <div className="min-h-screen bg-background" data-testid={`seo-page-${configKey}`}>
      <Header />

      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-[188px]">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt={`${cfg.h1} — AeroAcres FAA Part 137 certified drone application`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-12 pb-20 md:pt-16 md:pb-28 text-primary-foreground">
          <div className="text-primary-foreground/80">
            <Breadcrumbs items={[{ label: cfg.h1 }]} />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3.5 py-1.5 text-xs font-semibold tracking-wide border border-white/20"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" /> FAA Part 137 · AeroAcres South Florida
          </motion.div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-4xl tracking-tight">
            {cfg.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-primary-foreground/85 leading-relaxed">
            {cfg.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button asChild className="rounded-full bg-accent hover:bg-accent/90 h-12 px-7 text-base font-semibold">
              <Link to="/#contact">
                Get a free quote <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full bg-white/10 backdrop-blur border-white/30 hover:bg-white/20 text-white h-12 px-7 text-base font-semibold"
            >
              <a href={`tel:${BUSINESS.phoneRaw}`} data-conversion="phone-call" className="track-phone-click">
                <Phone className="h-4 w-4 mr-1.5" /> {BUSINESS.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <h2 className="font-display text-3xl sm:text-4xl text-primary font-bold">Why growers choose AeroAcres</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {cfg.bullets.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="flex gap-3 p-5 rounded-2xl bg-card border border-border shadow-card"
              >
                <div className="h-9 w-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center shrink-0">
                  <Check className="h-4 w-4" />
                </div>
                <p className="text-foreground/85 leading-relaxed text-sm md:text-base">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Crops */}
      <section className="py-20 md:py-28 bg-muted/40">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <h2 className="font-display text-3xl sm:text-4xl text-primary font-bold">Crops we routinely spray</h2>
          <div className="mt-8 flex flex-wrap gap-2">
            {cfg.crops.map((c) => (
              <span key={c} className="px-4 py-2 rounded-full bg-card border border-border text-sm font-semibold text-primary">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <MapPin className="h-10 w-10 text-accent mx-auto mb-5" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Ready to book your <span className="text-accent">drone spray</span>?
          </h2>
          <p className="mt-5 text-primary-foreground/80 max-w-2xl mx-auto">
            Submit a quick request — we respond within 24 hours with pricing, available slots and the right chemical program.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="rounded-full bg-accent hover:bg-accent/90 h-12 px-7 text-base font-semibold">
              <Link to="/#contact">Request a quote <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 h-12 px-7 text-base font-semibold">
              <a href={`tel:${BUSINESS.phoneRaw}`} data-conversion="phone-call" className="track-phone-click">
                <Phone className="h-4 w-4 mr-1.5" /> Call {BUSINESS.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Nearby links */}
      {cfg.nearby?.length > 0 && (
        <section className="py-16 md:py-20 bg-background">
          <div className="max-w-5xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-2xl text-primary font-bold">Related services & areas</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cfg.nearby.map((k) => {
                const link = k === "guide" ? { path: "/drone-spraying-florida-guide/", title: "South Florida Drone Spraying Guide" } : SEO_PAGES[k];
                if (!link) return null;
                return (
                  <Link key={k} to={link.path} className="group flex items-center justify-between p-4 rounded-xl bg-card border border-border hover:border-accent transition-colors">
                    <span className="font-semibold text-primary text-sm">{link.title.replace(" | AeroAcres", "")}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-accent" />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
